import { DialogProgrammatic as Dialog, ModalProgrammatic as Modal, NotificationProgrammatic as Notification } from 'buefy'
import { BModalComponent } from 'buefy/types/components';
import moment from "moment";
import i18n from "@/i18n";
import { ClientProject } from '@/models/ClientProject';
import { FilterCollection } from '@/models/FilterCollection';
import { GridColumn } from '@/models/GridColumn';
import { AdTitlesByLeads, Assignee, MultiselectItem, SelectListItem } from '@/models/MultiselectItem';
import { TableSettingsStateColumn } from '@/models/TableSettings';
import { UserData } from '@/models/UserData';

export default {

  //#region Date and time

  compareDateArrays(a: Date[], b: Date[]): boolean {
    return a.length === b.length &&
      a.every((element, index) => {
        return moment(element).format("YYYY-MM-DD") === moment(b[index]).format("YYYY-MM-DD");
      })
  },

  convertSecondsToMinutes(seconds: number): number {
    return <number><any>((seconds / 60).toFixed(2));
  },

  getDatePickerShortcuts(): any[] {
    return [
      {
        name: "today",
        text: i18n.t("Datepicker.Today").toString(),
        onClick(): Date[] {
          const start = moment().toDate();
          return [start, start];
        },
      },
      {
        name: "yesterday",
        text: i18n.t("Datepicker.Yesterday").toString(),
        onClick(): Date[] {
          const start = moment().subtract(1, 'days').toDate();
          return [start, start];
        },
      },
      {
        name: "prev_week",
        text: i18n.t("Datepicker.PreviousWeek").toString(),
        onClick(): Date[] {
          return [moment().subtract(7, 'days').toDate(), moment().toDate()];
        },
      },
      {
        name: "month",
        text: i18n.t("Datepicker.ThisMonth").toString(),
        onClick(): Date[] {
          return [moment().startOf('month').toDate(), moment().endOf('month').toDate()];
        },
      },
      {
        name: "prev_month",
        text: i18n.t("Datepicker.PreviousMonth").toString(),
        onClick(): Date[] {
          return [moment().subtract(1, 'month').startOf('month').toDate(), moment().subtract(1, 'month').endOf('month').toDate()];
        },
      },
      {
        name: "30_days",
        text: i18n.t("Datepicker.Last30Days").toString(),
        onClick(): Date[] {
          return [moment().subtract(30, 'days').toDate(), moment().toDate()];
        },
      },
      {
        name: "all_time",
        text: i18n.t("Datepicker.AllTime").toString(),
        onClick(): (Date | undefined)[] {
          return [undefined, undefined];
        },
      },
    ];
  },

  getThisMonthPeriod(): Date[] {
    let start = new Date();
    start.setDate(1);
    let end = new Date();
    if (end.getMonth() == 11) {
      end = new Date(end.getFullYear() + 1, 0, 1);
    } else {
      end = new Date(end.getFullYear(), end.getMonth() + 1, 1);
    }
    end.setDate(0);
    return [start, end];
  },

  getTimeStringFromSeconds(totalSeconds: number): string {
    const minutes: number = Math.floor(totalSeconds / 60);
    const seconds: number = totalSeconds - minutes * 60;
    return this.strPadLeft(minutes, '0', 2) + ':' + this.strPadLeft(seconds, '0', 2);
  },

  strPadLeft(s: number, pad: string, length: number) {
    return (new Array(length + 1).join(pad) + s).slice(-length);
  },

  //#endregion Date and time


  //#region Filters

  getFiltersString(filterCollection: FilterCollection): string {
    const filterStrings: string[] = [];
    const filterItemStrings: string[] = [];
    if (filterCollection.filters?.length) {
      for (const filter of filterCollection.filters) {
        filterItemStrings.push(this.getFiltersString(filter));
      }
    } else if (filterCollection.field) {
      filterItemStrings.push(`${filterCollection.field}~${filterCollection.operator}~${filterCollection.value}`);
    }
    if (filterItemStrings.length) {
      let filterItemString = "";
      if (filterItemStrings.length > 1) {
        filterItemString += "(";
      }
      filterItemString += filterItemStrings.join(`~${filterCollection.logic}~`);
      if (filterItemStrings.length > 1) {
        filterItemString += ")";
      }
      filterStrings.push(filterItemString);
    }
    return filterStrings.join(`~${filterCollection.logic}~`);
  },

  setFilterArray(fieldNames: string[], fieldValue: number[] | string[], operation: string, logic = "or"): FilterCollection {
    const filters: FilterCollection[] = [];
    for (const fieldValueItem of fieldValue) {
      for (const fieldName of fieldNames) {
        filters.push({
          field: fieldName,
          operator: operation,
          value: fieldValueItem
            ? typeof fieldValueItem === "string"
              ? `'${fieldValueItem}'`
              : fieldValueItem.toString()
            : undefined,
        });
      }
    }
    return {
      logic,
      filters: filters,
    };
  },

  //#endregion Filters


  //#region Locale

  isRtl(): boolean {
    return i18n.locale === "he-IL";
  },

  // for example, returns "en" from "en-US"
  getLanguageCodeWithoutCountry(locale: string): string {
    if (locale.indexOf("-") === -1) {
      return locale;
    } else {
      return locale.substr(0, locale.indexOf("-"));
    }
  },

  //#endregion Locale


  //#region Misc

  getAdTitleId(adTitle: AdTitlesByLeads) {
    return `${adTitle.AdGroupId}:${adTitle.AdId}`;
  },

  guid(): string {
    const s4 = Math.floor((1 + Math.random()) * 0x10000)
      .toString(16)
      .substring(1);
    return "ss-s-s-s-sss".replace(/s/g, s4);
  },

  //#endregion Misc


  //#region Notifications

  showConfirmDialog(title: string | undefined, message: string, confirmText: string, cancelText: string,
        onConfirm: () => void, onCancel?: (() => void) | undefined) {
    Dialog.confirm({
      cancelText,
      confirmText,
      message,
      title,
      onConfirm,
      onCancel,
    });
  },

  showModal(html: string, width: number, customClass: string, animation: string): BModalComponent {
    return Modal.open({
      animation,
      content: html,
      customClass,
      width
    });
  },

  showError(message: string, onConfirm?: () => void) {
    Dialog.alert({
        title: i18n.t("Error").toString(),
        message,
        type: "is-danger",
        ariaRole: "alertdialog",
        ariaModal: true,
        onConfirm
    })
  },

  showMessage(title: string, message: string, confirmText: string = "OK", onConfirm?: any) {
    Dialog.alert({
        title,
        message,
        confirmText,
        onConfirm
    })
  },

  showNotification(message: string,
        type?: "is-white" | "is-black" | "is-light" | "is-dark" | "is-primary" | "is-info" | "is-success" | "is-warning" | "is-danger",
        position?: "is-top-right" | "is-top" | "is-top-left" | "is-bottom-right" | "is-bottom" | "is-bottom-left",
        duration?: number) {
    if (!type) {
      type = "is-success";
    }
    if (!position) {
      position = "is-top-right";
    }
    if (!duration) {
      duration = 5000;
    }
    Notification.open({
      duration,
      message,
      position,
      type
    });
  },

  showSuccess(message: string, onConfirm?: () => void) {
    Dialog.alert({
        title: i18n.t("Success").toString(),
        message,
        type: "is-success",
        ariaRole: "alertdialog",
        ariaModal: true,
        onConfirm
    })
  },

  //#endregion Notifications


  //#region Sorting

  sortAdTitles(selectedItems: string[]) {
    const that = this;
    return function(a: AdTitlesByLeads, b: AdTitlesByLeads): number {
      const aSelected: boolean = selectedItems.indexOf(that.getAdTitleId(a)) >= 0;
      const bSelected: boolean = selectedItems.indexOf(that.getAdTitleId(b)) >= 0;
      if (aSelected && !bSelected) {
        return -1;
      } else if (!aSelected && bSelected) {
        return 1;
      } else {
        return 0;
      }
    }
  },

  sortAssets(selectedItems: string[]) {
    return function(a: ClientProject, b: ClientProject): number {
      const aSelected: boolean = selectedItems.indexOf(a.AssetId) >= 0;
      const bSelected: boolean = selectedItems.indexOf(b.AssetId) >= 0;
      if (aSelected && !bSelected) {
        return -1;
      } else if (!aSelected && bSelected) {
        return 1;
      } else {
        return 0;
      }
    }
  },

  sortAssignees(selectedItems: number[]) {
    return function(a: Assignee, b: Assignee): number {
      const aSelected: boolean = selectedItems.indexOf(+a.SalesmanID) >= 0;
      const bSelected: boolean = selectedItems.indexOf(+b.SalesmanID) >= 0;
      if (aSelected && !bSelected) {
        return -1;
      } else if (!aSelected && bSelected) {
        return 1;
      } else {
        return 0;
      }
    }
  },

  sortMultiselectItems(selectedItems: string[]) {
    return function(a: MultiselectItem, b: MultiselectItem): number {
      const aSelected: boolean = selectedItems.indexOf(a.ID) >= 0 || selectedItems.indexOf(a.Title) >= 0;
      const bSelected: boolean = selectedItems.indexOf(b.ID) >= 0 || selectedItems.indexOf(b.Title) >= 0;
      if (aSelected && !bSelected) {
        return -1;
      } else if (!aSelected && bSelected) {
        return 1;
      } else {
        return 0;
      }
    }
  },

  sortMultiselectItemsByIds(selectedItems: number[]) {
    return function(a: MultiselectItem, b: MultiselectItem): number {
      const aSelected: boolean = selectedItems.indexOf(+a.ID) >= 0;
      const bSelected: boolean = selectedItems.indexOf(+b.ID) >= 0;
      if (aSelected && !bSelected) {
        return -1;
      } else if (!aSelected && bSelected) {
        return 1;
      } else {
        return 0;
      }
    }
  },

  sortSelectListItems(selectedItems: number[]) {
    return function(a: SelectListItem, b: SelectListItem): number {
      const aSelected: boolean = selectedItems.indexOf(+a.Value) >= 0;
      const bSelected: boolean = selectedItems.indexOf(+b.Value) >= 0;
      if (aSelected && !bSelected) {
        return -1;
      } else if (!aSelected && bSelected) {
        return 1;
      } else {
        return 0;
      }
    }
  },

  //#endregion Sorting


  //#region Style

  getColumnStyle(column: GridColumn): string {
    let style = "";
    if (!this.isMobile() && column.width) {
      style += `min-width: ${ column.width }px`;
    }
    return style;
  },

  isMobile(): boolean {
    return window.innerWidth <= 768;
  },

  onDropdownActiveChange(show: boolean): void {
    if (show) {
      setTimeout(() => {
        const elButton: HTMLDivElement | null = document.querySelector(".dropdown.is-active");
        if (elButton) {
          const elButtonRect: DOMRect = elButton.getBoundingClientRect();
          const el: HTMLDivElement | null = elButton.querySelector(".dropdown-menu");
          if (el) {
            const elRect: DOMRect = el.getBoundingClientRect();
            if (elRect.width > 500) {
              el.style.width = "500px";
              el.classList.add("whitespace-normal")
            }
            if (!this.isRtl()) {
              if (elButtonRect.left + elRect.width > window.innerWidth) {
                el.style.left = "auto";
                el.style.right = "0";
              } else {
                el.style.left = "0";
                el.style.right = "auto";
              }
            } else {
              if (elButtonRect.right - elRect.width < 0) {
                el.style.left = "0";
                el.style.right = "auto";
              } else {
                el.style.left = "auto";
                el.style.right = "0";
              }
            }

            const searchInput: HTMLInputElement | null = el.querySelector(".search input");
            if (searchInput) {
              searchInput.focus();
            }
          }
        }
      });
    }
  },

  //#endregion Style


  //#region TableSettings

  createTableSettingsColumn(column: GridColumn): TableSettingsStateColumn {
    let format = "";
    if (column.name.endsWith("Date")) {
      format = "{0:dd/MM/yyyy HH:mm}";
    }
    return {
      encoded: true,
      field: column.name,
      format,
      hidden: !column.visible,
      locked: column.name === "ID",
      menu: false,
      sortable: column.visible,
      template: "",
      title: column.label,
      width: "",
      attributes: {
        style: ""
      },
      filterable: false,
      footerAttributes: {
        style: ""
      },
      headerAttributes: {
        id: "",
        style: ""
      },
    };
  },

  onColumnSelectInitSettingsColumn(settingsColumn: TableSettingsStateColumn, column: GridColumn) {
    settingsColumn.hidden = !column.visible;
    if (settingsColumn.attributes === undefined) {
      settingsColumn.attributes = {
        style: ""
      };
    }
    if (settingsColumn.footerAttributes === undefined) {
      settingsColumn.footerAttributes = {
        style: ""
      };
    }
    if (settingsColumn.headerAttributes === undefined) {
      settingsColumn.headerAttributes = {
        style: ""
      };
    }
    if (!column.visible) {
      settingsColumn.attributes.style = settingsColumn.footerAttributes.style = settingsColumn.headerAttributes.style = "display:none";
    } else {
      settingsColumn.attributes.style = settingsColumn.footerAttributes.style = settingsColumn.headerAttributes.style = "";
    }
  },

  //#endregion TableSettings


  //#region User

  isUserInRole(userData: UserData, role: string): boolean {
    if (!userData || !userData.Roles) {
      return false;
    } else {
      return userData.Roles.indexOf(role) >= 0;
    }
  }

  //#endregion User
};
